import { useState } from 'react'
import { formatSize, normalizedText } from '@domain/product'
import type { Product } from '@domain/product'
import { EscolhaEmTelaCheia } from '@ui/shell/EscolhaEmTelaCheia'
import { Miniatura } from '@ui/produtos/Miniatura'
import { IconeCheque } from '@ui/shell/icones'

/**
 * Escolher o produto de uma linha da nota.
 *
 * ## Foto, marca e tamanho em cada opção
 *
 * A primeira versão eram pílulas com o nome, e o catálogo tem três "Água com
 * gás": Santa Catarina 500 ml, EcoSpring 500 ml, e assim por diante. Três
 * pílulas iguais lado a lado não se escolhem, se adivinham — e o vínculo
 * errado vira aprendizado, que faz a próxima nota errar sozinha. O que
 * distingue embalagens é exatamente o que a lista de Produtos mostra: a foto,
 * a marca e o tamanho.
 *
 * ## Tela cheia, e não pílulas embaixo da linha
 *
 * As pílulas cresciam dentro da linha e empurravam as seguintes; com foto e
 * duas linhas de texto por opção, cresceriam o triplo. É o mesmo painel da
 * categoria, com a busca e as setas do teclado.
 *
 * ## A busca olha nome E marca
 *
 * "santa catarina" é como alguém procura uma água de marca. Pela mesma
 * normalização do catálogo: "agua" acha "Água".
 */
export function EscolherProduto({
  rawText,
  produtos,
  atual,
  onEscolher,
  onFechar,
}: {
  /** A linha da nota, para o nome do diálogo. */
  rawText: string
  produtos: Product[]
  /** O produto que a linha já tem, marcado na lista. */
  atual: string | null
  onEscolher: (produto: Product) => void
  onFechar: () => void
}) {
  const [busca, setBusca] = useState('')

  const termo = normalizedText(busca)
  const opcoes = produtos
    .filter((p) => p.archivedAt === null)
    .filter(
      (p) =>
        termo === '' ||
        p.normalizedName.includes(termo) ||
        (p.brand !== null && normalizedText(p.brand).includes(termo)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

  return (
    <EscolhaEmTelaCheia
      titulo="Produto"
      rotulo={`Escolher o produto de ${rawText}`}
      rotuloDaBusca="Buscar produto"
      placeholder="Buscar pelo nome ou marca…"
      busca={busca}
      onBuscar={setBusca}
      onFechar={onFechar}
    >
      {opcoes.length === 0 ? (
        <p className="px-2 py-4 text-sm text-[var(--text-muted)]">
          Nenhum produto com esse nome. Cadastre em Produtos e volte — o vínculo fica aprendido
          para as próximas notas.
        </p>
      ) : (
        <ul aria-label="Produtos do catálogo">
          {opcoes.map((p) => {
            const detalhe = [p.brand, formatSize(p.size, p.unit, p.packQuantity)]
              .filter((parte) => parte !== null && parte !== '')
              .join(' · ')
            return (
              <li key={p.id}>
                <button
                  type="button"
                  aria-current={p.id === atual ? 'true' : undefined}
                  onClick={() => {
                    onEscolher(p)
                  }}
                  className="alvo w-full justify-start gap-3 border-b border-[var(--border)] px-2 py-2 text-left text-sm"
                >
                  <Miniatura nome={p.name} src={p.imageUrl} className="size-12" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{p.name}</span>
                    <span className="block truncate text-xs text-[var(--text-muted)]">
                      {detalhe === '' ? 'sem marca nem tamanho' : detalhe}
                    </span>
                  </span>
                  {p.id === atual && (
                    <span className="shrink-0 text-[var(--brand)]">
                      <IconeCheque />
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </EscolhaEmTelaCheia>
  )
}
